/**
 * API client.
 *
 * Every mutating request sends `Origin` implicitly (the browser sets it) and
 * `credentials: 'same-origin'` so the `__Host-` session cookie travels. A 401
 * is not an error to surface — it is the signal that drives the sign-in screen
 * (§4.3), so it is modelled explicitly rather than thrown.
 */

import type { Band, MatchState } from '@clotho/core';

export class Unauthenticated extends Error {
  constructor() {
    super('unauthenticated');
  }
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly detail?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (response.status === 401) throw new Unauthenticated();

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { error?: { code?: string; message?: string; detail?: unknown } }
      | null;
    throw new ApiError(
      body?.error?.code ?? 'unknown',
      body?.error?.message ?? `Request failed (${response.status})`,
      response.status,
      body?.error?.detail,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const get = <T>(path: string): Promise<T> => request<T>(path);
const send = <T>(method: string, path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });

// ---------------------------------------------------------------------------
// Shapes the UI consumes. These mirror the server's response bodies.
// ---------------------------------------------------------------------------

export interface Me {
  id: string;
  email: string;
  name: string | null;
  pictureUrl: string | null;
}

export interface SessionSummary {
  id: string;
  createdAt: number;
  lastSeenAt: number;
  expiresAt: number;
  userAgent: string | null;
  current: boolean;
}

export interface ListingView {
  id: string;
  sourceId: string;
  url: string;
  title: string;
  brandRaw: string | null;
  brandKey: string | null;
  categoryKey: string;
  sizeRaw: string | null;
  sizeTokens: unknown[];
  condition: string;
  price: { cents: number; currency: string };
  priceUsdCents: number;
  originalPriceUsdCents: number | null;
  minPriceUsdCents: number;
  imageUrl: string | null;
  imageKey: string | null;
  fuzzyKey: string;
  status: string;
}

export interface MatchView {
  id: string;
  profileId: string;
  score: number;
  band: Band;
  heuristicScore: number;
  llmScore: number | null;
  state: MatchState;
  reasons: string[];
  flags: { llmFlags?: string[]; softRulePenalties?: string[]; skipped?: string[] } | null;
  firstMatchedAt: number;
  digestedAt: number | null;
  listing: ListingView;
}

export interface SignalView {
  key: string;
  value: number | null;
  weight: number;
  reason: string;
}

export interface MatchDetailView extends MatchView {
  signals: SignalView[];
  savedPriceUsdCents: number | null;
}

export interface SourceView {
  id: string;
  label: string;
  tier: string;
  enabled: boolean;
  health: string;
  consecutiveFailures: number;
  lastOkAt: number | null;
  lastError: string | null;
  disabledUntil: number | null;
  config: unknown;
  listingCount: number;
}

export interface RunView {
  id: string;
  startedAt: number;
  finishedAt: number | null;
  status: string;
  trigger: string;
  stats: Record<string, unknown> | null;
  budget: {
    spentUsdMicros: number;
    degradations: string[];
    refusals: number;
    tokensIn: number;
    tokensOut: number;
  } | null;
  aiSpentUsdMicros: number;
}

export interface DigestSummary {
  id: string;
  profileId: string;
  profileName: string;
  sentAt: number | null;
  itemCount: number;
  error?: string | null;
  createdAt: number;
}

export interface MatchQuery {
  profileId?: string;
  band?: Band[];
  state?: MatchState[];
  sourceId?: string[];
  minScore?: number;
  maxPriceUsd?: number;
  q?: string;
  sort?: 'score' | 'price' | 'new';
  cursor?: string;
  limit?: number;
}

function toQueryString(query: MatchQuery): string {
  const params = new URLSearchParams();
  if (query.profileId) params.set('profileId', query.profileId);
  if (query.band?.length) params.set('band', query.band.join(','));
  if (query.state?.length) params.set('state', query.state.join(','));
  if (query.sourceId?.length) params.set('sourceId', query.sourceId.join(','));
  if (query.minScore !== undefined) params.set('minScore', String(query.minScore));
  if (query.maxPriceUsd !== undefined) params.set('maxPriceUsd', String(query.maxPriceUsd));
  if (query.q) params.set('q', query.q);
  if (query.sort) params.set('sort', query.sort);
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.limit) params.set('limit', String(query.limit));
  return params.toString();
}

export const api = {
  me: () => get<Me>('/api/me'),
  sessions: () => get<{ sessions: SessionSummary[] }>('/api/me/sessions'),
  logout: () => send<{ ok: boolean }>('POST', '/api/me/logout'),
  logoutAll: () => send<{ ok: boolean }>('POST', '/api/me/logout-all'),

  profiles: () => get<{ profiles: Record<string, unknown>[] }>('/api/profiles'),
  profile: (id: string) => get<{ profile: Record<string, unknown> }>(`/api/profiles/${id}`),
  createProfile: (body: unknown) => send<{ profile: { id: string } }>('POST', '/api/profiles', body),
  updateProfile: (id: string, body: unknown) =>
    send<{ profile: Record<string, unknown> }>('PUT', `/api/profiles/${id}`, body),
  deleteProfile: (id: string) => send<{ ok: boolean }>('DELETE', `/api/profiles/${id}`),
  compileProfile: (id: string) => send<{ jobId: string }>('POST', `/api/profiles/${id}/compile`),
  putCompiledRules: (id: string, compiledRules: unknown) =>
    send<{ compiledRules: unknown }>('PUT', `/api/profiles/${id}/compiled-rules`, { compiledRules }),

  matches: (query: MatchQuery = {}) =>
    get<{ matches: MatchView[]; nextCursor: string | null }>(`/api/matches?${toQueryString(query)}`),
  match: (id: string) =>
    get<{ match: MatchDetailView; siblings: { listingId: string; sourceId: string; url: string; priceUsdCents: number; status: string }[] }>(
      `/api/matches/${id}`,
    ),
  setMatchState: (id: string, state: MatchState) =>
    send<{ ok: boolean }>('PATCH', `/api/matches/${id}`, { state }),
  sendFeedback: (id: string, verdict: string, note?: string) =>
    send<{ ok: boolean }>('POST', `/api/matches/${id}/feedback`, { verdict, ...(note ? { note } : {}) }),

  digests: (profileId?: string) =>
    get<{ digests: DigestSummary[] }>(`/api/digests${profileId ? `?profileId=${profileId}` : ''}`),
  latestDigests: () =>
    get<{ digests: DigestSummary[]; lastRunAt: number | null; stale: boolean }>('/api/digests/latest'),
  digest: (id: string) =>
    get<{ digest: DigestSummary; items: (MatchView & { section: string; rank: number })[] }>(
      `/api/digests/${id}`,
    ),

  sources: () => get<{ sources: SourceView[] }>('/api/sources'),
  createSource: (body: unknown) => send<{ ok: boolean; id: string }>('POST', '/api/sources', body),
  updateSource: (id: string, body: unknown) => send<{ ok: boolean }>('PUT', `/api/sources/${id}`, body),
  deleteSource: (id: string) => send<{ ok: boolean }>('DELETE', `/api/sources/${id}`),
  previewSource: (config: unknown, query?: string) =>
    send<{ jobId: string }>('POST', '/api/sources/preview', { config, ...(query ? { query } : {}) }),
  job: (id: string) =>
    get<{ job: { id: string; status: string; result: unknown; error: string | null } }>(`/api/jobs/${id}`),

  runs: () =>
    get<{ runs: RunView[]; aiSpentTodayUsdMicros: number; runNowAvailable: boolean }>('/api/runs'),
  runErrors: (id: string) =>
    get<{ errors: { id: string; sourceId: string | null; stage: string; message: string; detail: string | null; createdAt: number }[] }>(
      `/api/runs/${id}/errors`,
    ),
  triggerRun: () => send<{ ok: boolean }>('POST', '/api/runs/trigger'),

  settings: () =>
    get<{ settings: Record<string, unknown>; runNowAvailable: boolean; mailConfigured: boolean; allowlistEditable: boolean }>(
      '/api/settings',
    ),
  updateSettings: (body: unknown) => send<{ settings: Record<string, unknown> }>('PUT', '/api/settings', body),

  brandSuggestions: () =>
    get<{ suggestions: { brandRaw: string; count: number }[] }>('/api/brand-suggestions'),
  addBrandAlias: (alias: string, brandKey: string) =>
    send<{ ok: boolean }>('POST', '/api/brand-aliases', { alias, brandKey }),
};

/** Proxied R2 read (§4.4): never a public bucket URL. */
export function imageSrc(match: { listing: Pick<ListingView, 'imageKey' | 'imageUrl'> }): string | null {
  if (match.listing.imageKey) return `/api/images/${encodeURIComponent(match.listing.imageKey)}`;
  return match.listing.imageUrl;
}
